// One attribute option's value as a person reads it: a number in its display unit
// ("1,270 kWp"), a date ("20 Oct 2027"), a stage ("in negotiation"), a person
// ("Jo Bloggs (Savills, jo@savills.com)") or, with no figure, what the definition calls
// that ("no DC design"). Mirrors project_attributes.format_person. Shared by the
// dashboard and the Attributes tab so the two cannot disagree. A stage's label comes
// from the definition, so the backend's wording is the only one.
import type {
  AttributeDef,
  AttributeValue,
  OptionValue,
  PersonValue,
} from "../api/hooks/useDashboard";

type Figure = number | string;

export const isFigure = (v: OptionValue): v is Figure =>
  typeof v === "number" || typeof v === "string";

// The smallest or earliest figure among a value's options, as the schedule test reads a
// committed value; undefined when unset or no option carries a figure.
export const smallestFigure = (
  value: AttributeValue | undefined,
): Figure | undefined => {
  const figures = (value?.options ?? []).map((o) => o.value).filter(isFigure);
  return figures.length
    ? figures.reduce((a, b) =>
        String(b).localeCompare(String(a), undefined, { numeric: true }) < 0
          ? b
          : a,
      )
    : undefined;
};

export const formatDay = (iso: string): string => {
  const parsed = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (!iso || Number.isNaN(parsed)) return iso;
  // Some engines abbreviate September as "Sept"; one form everywhere.
  return new Date(parsed)
    .toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    })
    .replace("Sept", "Sep");
};

// "12 Oct", with the year only when it is not this year's.
export const formatShortDay = (iso: string): string =>
  iso.slice(0, 4) === String(new Date().getFullYear())
    ? formatDay(iso).replace(/ \d{4}$/, "")
    : formatDay(iso);

// The LOCAL calendar day as an ISO date. toISOString is UTC, which names the previous
// day between midnight and 1am BST.
export const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// An ISO date as local midnight, the Date a calendar control shows as that day.
export const localDay = (iso: string): Date => new Date(`${iso.slice(0, 10)}T00:00:00`);

export const formatPerson = (person: PersonValue): string => {
  const detail = [person.org, person.email].filter(Boolean).join(", ");
  return person.name ? `${person.name} (${detail})` : detail;
};

export const formatOptionValue = (
  definition: AttributeDef,
  value: OptionValue,
): string => {
  if (value === null) return definition.no_figure;
  if (typeof value === "object") return formatPerson(value);
  if (definition.kind === "date") return formatDay(String(value));
  if (definition.kind === "stage")
    return (
      definition.choice_labels[String(value)] ??
      String(value).replace(/_/g, " ")
    );
  const shown = Number(value) * definition.display_scale;
  return `${shown.toLocaleString("en-GB", {
    minimumFractionDigits: definition.decimals,
    maximumFractionDigits: definition.decimals,
  })} ${definition.display_unit}`;
};
